import { useEffect, useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import {
  type AddressFormFields,
  BLANK_ADDRESS_FORM,
  type FieldErrors,
  validateAddressFields,
} from './checkout-domain'

export interface AddressFormModalProps {
  readonly open: boolean
  readonly onClose: () => void
  /** Present when editing a saved address; absent for "+ Add new address". */
  readonly initialFields?: AddressFormFields | undefined
  readonly onSubmit: (fields: AddressFormFields, isDefault?: boolean) => Promise<void>
}

/**
 * Add/Edit address modal (UID-97 K2, ADR-0044 §2). Matches the prototype's
 * Add/Edit address modal (`Fictiv Platform.dc.html` lines 3791-3804):
 * Label, then a Full name + Company row (the K4 fields), then the street/
 * city/state/ZIP/country value fields. Deliberately does NOT include a
 * "set as default" checkbox here — that is the separate
 * `POST /addresses/{id}/default` op (ADR-0044 §2), kept off the edit form so
 * an edit can never silently flip the default.
 */
export function AddressFormModal({
  open,
  onClose,
  initialFields,
  onSubmit,
}: AddressFormModalProps) {
  const isEdit = initialFields !== undefined
  const [fields, setFields] = useState<AddressFormFields>(initialFields ?? BLANK_ADDRESS_FORM)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [busy, setBusy] = useState(false)
  // Phone is collected for design parity but has no address-contract field, so
  // it's never sent (mirrors the cart's AddAddressModal). isDefault feeds the
  // create request's "set as default" (add mode only).
  const [phone, setPhone] = useState('')
  const [isDefault, setIsDefault] = useState(false)

  // Deliberately depends on `open` ONLY (not `initialFields`): the caller
  // (ShippingAddressStep) derives `initialFields` fresh via
  // `addressFormFieldsFrom` on every render, so a NEW object identity
  // reaches this component on every parent re-render while the modal is
  // already open. Re-seeding on that would silently blow away in-progress
  // edits mid-typing. Seed once, when the modal transitions to open; the
  // effect body still reads the freshest `initialFields` value at that
  // moment (a stale-closure lint would be a false positive here).
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally open-only, see comment above.
  useEffect(() => {
    if (open) {
      setFields(initialFields ?? BLANK_ADDRESS_FORM)
      setErrors({})
      setPhone('')
      setIsDefault(false)
    }
  }, [open])

  function updateField(name: keyof AddressFormFields, value: string) {
    setFields((current) => ({ ...current, [name]: value }))
    // Clear this field's error the moment the buyer edits it, so a stale
    // "required" message doesn't linger while they fix it (mirrors the
    // Payment step's change-clears-error behaviour). Re-validation still runs
    // in full on the next submit.
    setErrors((current) => {
      if (!current[name]) return current
      const next = { ...current }
      delete next[name]
      return next
    })
  }

  async function handleSubmit() {
    const nextErrors = validateAddressFields(fields)
    if (Object.values(nextErrors).some(Boolean)) {
      setErrors(nextErrors)
      return
    }
    setBusy(true)
    try {
      await onSubmit(fields, isDefault)
      onClose()
    } catch {
      // Caller surfaces the failure via toast; keep the modal open so the
      // buyer can retry without re-typing everything.
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit address' : 'Add address'}
      maxWidth={540}
      footer={
        <>
          <Button variant="outline" size="lg" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {/* The prototype's modal footer button reads "Save address"
              unconditionally (`Fictiv Platform.dc.html` L4203), even when
              editing — matched here rather than a conditional "Save changes". */}
          <Button size="lg" onClick={() => void handleSubmit()} loading={busy}>
            Save address
          </Button>
        </>
      }
    >
      <div className="checkout-address-form-fields">
        <Input
          label="Label"
          placeholder="e.g. HQ, machine shop"
          value={fields.label}
          onChange={(event) => updateField('label', event.target.value)}
          error={errors.label}
        />
        <div className="checkout-address-form-row">
          <Input
            label="Full name"
            value={fields.contactName}
            onChange={(event) => updateField('contactName', event.target.value)}
          />
          <Input
            label="Company"
            value={fields.companyName}
            onChange={(event) => updateField('companyName', event.target.value)}
          />
        </div>
        <Input
          label="Street address"
          value={fields.line1}
          onChange={(event) => updateField('line1', event.target.value)}
          error={errors.line1}
        />
        <Input
          label="Apt, suite, etc. (optional)"
          value={fields.line2}
          onChange={(event) => updateField('line2', event.target.value)}
        />
        <div className="checkout-address-form-row-3">
          <Input
            label="City"
            value={fields.city}
            onChange={(event) => updateField('city', event.target.value)}
            error={errors.city}
          />
          <Input
            label="State"
            value={fields.region}
            onChange={(event) => updateField('region', event.target.value)}
            error={errors.region}
          />
          <Input
            label="ZIP"
            value={fields.postalCode}
            onChange={(event) => updateField('postalCode', event.target.value)}
            error={errors.postalCode}
          />
        </div>
        <Input label="Phone" value={phone} onChange={(event) => setPhone(event.target.value)} />
        {!isEdit && (
          <label className="checkout-set-default-row">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(event) => setIsDefault(event.target.checked)}
            />
            Set as default shipping address
          </label>
        )}
      </div>
    </Modal>
  )
}
